import React, { useEffect, useState } from 'react';
import { MessageSquare, Heart, Share2, Trash2, Clock, User, UtensilsCrossed, Eye, X } from 'lucide-react';
import { useFeedStore } from '../store/feedStore';
import { useAuth } from '../hooks/useAuth';
import { format } from 'date-fns';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { RecipePreview } from '../components/RecipePreview';
import { useRecipeStore } from '../store/recipeStore';
import axios from 'axios';
interface FeedPageProps {
  onImport: (sourceUrl: string) => void;
}

export function FeedPage({onImport}: FeedPageProps) {
  const { posts, loading, error, fetchPosts, deletePost } = useFeedStore();
  const { addRecipe } = useRecipeStore();
  const { user } = useAuth();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<any>(null);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleDelete = async (postId: string) => {
    await deletePost(postId);
    setShowDeleteConfirm(null);
  };

  const handlePreview = async (sourceUrl: string) => {
    onImport(sourceUrl);
    // setShowPreview(recipeId);
    // setPreviewLoading(true);
    // setPreviewError(null);
    // setPreviewData(null);

    // try {
    //   const { data } = await axios.get(`/api/preview-recipe?id=${recipeId}`);
    //   setPreviewData(data);
    // } catch (err) {
    //   console.error('Preview error:', err);
    //   setPreviewError(err instanceof Error ? err.message : 'Failed to load preview');
    // } finally {
    //   setPreviewLoading(false);
    // }
  };

  const handleImport = async (recipe: any) => {
    try {
      await addRecipe(recipe);
      setShowPreview(null);
    } catch (err) {
      console.error('Import error:', err);
    }
  };

  if (loading && posts.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-gray-600">Loading feed...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 text-center">
        <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No Posts Yet</h2>
        <p className="text-gray-600">
          Share your recipes and meals with friends to see them here!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {posts.map((post) => (
        <div key={post.id} className="bg-white rounded-xl shadow-sm">
          {/* Post Header */}
          <div className="p-4 flex items-center justify-between border-b">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-gray-500" />
              </div>
              <div>
                <div className="font-medium">{post.user_profile?.display_name || post.user_profile?.email}</div>
                <div className="text-sm text-gray-500 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {format(new Date(post.created_at), 'MMM d, yyyy')}
                </div>
              </div>
            </div>
            {post.user_id === user?.id && (
              <div className="relative">
                <button
                  onClick={() => setShowDeleteConfirm(showDeleteConfirm === post.id ? null : post.id)}
                  className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                {showDeleteConfirm === post.id && (
                  <ConfirmationModal
                    title="Delete Post"
                    message="Are you sure you want to delete this post?"
                    confirmLabel="Delete"
                    onConfirm={() => handleDelete(post.id)}
                    onCancel={() => setShowDeleteConfirm(null)}
                    isDestructive
                  />
                )}
              </div>
            )}
          </div>

          {/* Post Content */}
          <div className="p-4">
            {post.content && (
              <p className="text-gray-800 whitespace-pre-wrap mb-4">{post.content}</p>
            )}
            
            {/* Recipe Image */}
            {post.image_url && (
              <div className="mb-4 relative group">
                <img
                  src={post.image_url}
                  alt="Recipe"
                  className="w-full aspect-square object-cover rounded-lg"
                />
                {post.recipe_id && (
                  <button
                    onClick={() => handlePreview(post.source_url)}
                    className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all"
                  >
                    <Eye 
                      className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-all" 
                    />
                  </button>
                )}
              </div>
            )}

            {/* Tagged Users */}
            {post.tagged_users && post.tagged_users.length > 0 && (
              <div className="mb-4 flex flex-wrap gap-2">
                {post.tagged_users.map((userId, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                  >
                    <User size={14} />
                    {userId}
                  </span>
                ))}
              </div>
            )}
            
            {/* Linked Content */}
            {(post.recipe_title || post.meal_name) && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg flex items-center gap-3">
                {post.recipe_title ? (
                  <>
                    <UtensilsCrossed className="w-5 h-5 text-blue-600" />
                    <div>
                      <div className="text-sm text-gray-500">Recipe</div>
                      <div className="font-medium">{post.recipe_title}</div>
                    </div>
                  </>
                ) : post.meal_name ? (
                  <>
                    <UtensilsCrossed className="w-5 h-5 text-purple-600" />
                    <div>
                      <div className="text-sm text-gray-500">Meal</div>
                      <div className="font-medium">{post.meal_name}</div>
                    </div>
                  </>
                ) : null}
              </div>
            )}
          </div>

          {/* Post Actions */}
          <div className="px-4 py-3 border-t flex items-center gap-6">
            <button className="flex items-center gap-2 text-gray-600 hover:text-red-600">
              <Heart className="w-5 h-5" />
              <span className="text-sm">Like</span>
            </button>
            <button className="flex items-center gap-2 text-gray-600 hover:text-blue-600">
              <Share2 className="w-5 h-5" />
              <span className="text-sm">Share</span>
            </button>
          </div>
        </div>
      ))}

      {/* Recipe Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowPreview(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Recipe Preview</h3>
              <button
                onClick={() => setShowPreview(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            <RecipePreview
              recipe={previewData}
              loading={previewLoading}
              error={previewError}
            />
            {previewData && !previewLoading && !previewError && (
              <div className="sticky bottom-0 bg-white border-t p-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowPreview(null)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                >
                  Close
                </button>
                <button
                  onClick={() => handleImport(previewData)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Import Recipe
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}